import ReactDOM from "react-dom/client";
import MarkDownText from "../components/MarkDownText/MarkDownText";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

interface Props {
  data: any;
  respuestaImg: string;
}

const OpenMarkdownPreview = ({ data, respuestaImg }: Props) => {
  const newWindow = window.open("", "_blank");

  if (!newWindow) {
    alert("No se pudo abrir la ventana de vista previa.");
    return;
  }

  // Links externos (fuente Montserrat + KaTeX)
  const externalLinks = `
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link
      href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap"
      rel="stylesheet"
    />
    <link
      rel="stylesheet"
      href="https://cdn.jsdelivr.net/npm/katex@0.16.11/dist/katex.min.css"
      integrity="sha384-Rb9HhtfKQy9yE4zRzSglKzQJ2OEQnZt2H3bqCkJ6M2b8e7dO6kGm8r6dVwH2eQw6"
      crossorigin="anonymous"
    />
  `;

  // Estilos base + impresión
  const styles = `
   <style>
  :root { --brand-blue: #336B87; --thead-bg: #90AFC5; }
  @page { size: A4; margin: 16mm; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    a[href] { text-decoration: none; color: inherit; }
    .MuiTooltip-popper, .download-btn { display: none !important; }
    th, td { font-size: 14px; } /* un poco más compacto en PDF */
  }

  html, body {
    font-family: 'Montserrat', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
    font-size: 16px;
    color: rgba(0,0,0,0.8);
    margin: 0;
    padding: 20px;
  }
  h1, h2, h3, h4, h5, h6 { font-weight: 700; color: var(--brand-blue); margin: 0.6em 0 0.4em; }
  p, li, td { text-align: justify; }
  img { max-width: 100%; height: auto; }

  /* ===== Base actual ===== */
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 8px; }
  thead th { background-color: var(--thead-bg); color: #fff; }

  .custom-table tr:nth-child(even) { background-color: rgba(144, 175, 197, 0.3); }
  .custom-table tr:nth-child(odd)  { background-color: #FFF; }
  .custom-table tr:nth-child(even) td:first-child {
    border-top-left-radius: 6px; border-bottom-left-radius: 6px;
  }
  .custom-table tr:nth-child(even) td:last-child {
    border-top-right-radius: 6px; border-bottom-right-radius: 6px;
  }

  /* ===== Mejoras para impresión de tablas ===== */

  /* Reparto de ancho consistente */
  table { table-layout: fixed; page-break-inside: auto; }

  /* Encabezado se repite en cada página / pie opcional */
  thead { display: table-header-group; }
  tfoot { display: table-footer-group; }

  /* Evitar cortar filas a la mitad */
  tr { break-inside: avoid; page-break-inside: avoid; }

  /* Envolver texto largo dentro de celdas */
  th, td {
    vertical-align: top;
    word-break: break-word;
    overflow-wrap: anywhere;
    hyphens: auto;
    line-height: 1.45;
    font-family: 'Montserrat', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; /* asegura Montserrat en tablas */
  }

  /* Si tu tabla suele tener 4 columnas, da anchos orientativos (ajústalos si quieres) */
  thead th:nth-child(1), tbody td:nth-child(1) { width: 20%; }
  thead th:nth-child(2), tbody td:nth-child(2) { width: 25%; }
  thead th:nth-child(3), tbody td:nth-child(3) { width: 35%; } /* columna larga */
  thead th:nth-child(4), tbody td:nth-child(4) { width: 20%; }
</style>

  `;

  // Documento base de la ventana nueva
  newWindow.document.open();
  newWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>UmmIA - Proyecto</title>
        <link rel="icon" href="./icon/LOGO_TUCLASE.ico" />
        ${externalLinks}
        ${styles}
      </head>
      <body>
        <div id="content"></div>
      </body>
    </html>
  `);
  newWindow.document.close();

  // Renderizar el componente dentro de la nueva ventana
  newWindow.onload = () => {
    const contentElement = newWindow.document.getElementById("content");

    if (contentElement) {
      // 🔧 Cache de Emotion apuntando al <head> de la nueva ventana (para estilos MUI)
      const cache = createCache({
        key: "printwin",
        container: newWindow.document.head,
        prepend: true,
      });

      const root = ReactDOM.createRoot(contentElement);

      // Si NO usas MUI/Emotion dentro del preview, puedes renderizar <MarkDownText ... /> directamente.
      root.render(
        <CacheProvider value={cache}>
          <MarkDownText data={data} img={respuestaImg} />
        </CacheProvider>
      );

      // Imprimir y cerrar
      const handlePrintAndClose = () => {
        const closeNewWindow = () => newWindow.close();
        const handleFocus = () => setTimeout(closeNewWindow, 500);

        newWindow.print();
        newWindow.onfocus = handleFocus;
      };

      // Espera breve para asegurarse de que se montó todo (fuentes/estilos)
      setTimeout(handlePrintAndClose, 600);
    } else {
      console.error("No se encontró el elemento 'content' en la nueva ventana.");
    }
  };
};

export default OpenMarkdownPreview;
